/* eslint-disable @typescript-eslint/naming-convention */
import { HighlightIcon, Tag, DATE_FORMAT } from '@dev-spendesk/grapes';
import cx from 'classnames';
import { type MonetaryValue, toNumber } from 'ezmoney';
import React from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { BudgetAmountEstimation } from './BudgetAmountEstimation';
import {
  type BudgetBreakdown,
  getSpentBreakdownRatio,
  hasExceededAmounts,
} from '../../models/breakdown';
import { Percentage } from '../Percentage';

type Props = {
  title?: string;
  name: string;
  amount: MonetaryValue;
  breakdown: BudgetBreakdown;
  isUnavailable?: boolean;
  withDate?: boolean;
  mode: 'compact' | 'medium' | 'full';
  date?: Date;
  hint?: string;
};

export const BudgetOverviewHeader = ({
  title,
  name,
  amount,
  breakdown,
  isUnavailable,
  withDate,
  mode,
  date,
  hint,
}: Props) => {
  const { localeFormat, t } = useTranslation('global');
  const isExceeded = hasExceededAmounts(breakdown);

  const isCompact = mode === 'compact';

  return (
    <div className="w-full text-left">
      <div
        className={cx(
          'text-content-primary title-l mb-16 flex w-full items-center justify-between',
        )}
      >
        <div className="flex max-w-[345px] items-center overflow-hidden text-ellipsis">
          <span className={cx('text-content-primary title-l truncate')}>
            {title ?? name}{' '}
            {withDate && date && (
              <span className="text-content-primary title-l">
                (
                {localeFormat(date, DATE_FORMAT.CUSTOM, {
                  month: 'long',
                  year: 'numeric',
                })}
                )
              </span>
            )}
          </span>
          {isUnavailable && (
            <Tag className="ml-8" variant="neutral">
              {t('budget.unavailable')}
            </Tag>
          )}
        </div>
        <div
          className={cx(
            'flex h-16 items-center',
            isCompact ? 'body-m' : 'body-l',
            { 'text-content-warning-default': isExceeded },
          )}
        >
          {isExceeded && (
            <HighlightIcon
              className="mr-8"
              name="triangle-warning"
              size={24}
              variant="warning"
            />
          )}
          <div>
            {toNumber(amount) > 0 ? (
              <Percentage ratio={getSpentBreakdownRatio(breakdown)} />
            ) : (
              ''
            )}
          </div>
        </div>
      </div>
      <BudgetAmountEstimation
        amount={amount}
        breakdown={breakdown}
        hint={hint}
      />
    </div>
  );
};
